import { useScrollAnimation } from '@/hooks/useScrollAnimation';

export interface WhyChooseUsProps {
  image: string;
  eyebrow: string;
  headline: string;
  benefits: { icon: string; title: string; description: string }[];
  cta?: { text: string; href: string };
}

export default function WhyChooseUs({ image, eyebrow, headline, benefits, cta }: WhyChooseUsProps) {
  const imgAnim = useScrollAnimation(0.1);
  const textAnim = useScrollAnimation(0.1);

  return (
    <section className="bg-white overflow-hidden">
      <div className="flex flex-col lg:flex-row min-h-[640px]">

        {/* Image panel */}
        <div
          ref={imgAnim.ref}
          className="w-full lg:w-[45%] relative overflow-hidden"
          style={{
            minHeight: '400px',
            opacity: imgAnim.visible ? 1 : 0,
            transform: imgAnim.visible ? 'translateX(0)' : 'translateX(-40px)',
            transition: 'opacity 0.8s ease, transform 0.8s ease',
          }}
        >
          <img src={image} alt="Chef Joyce Cunha" className="w-full h-full object-cover object-top" style={{ minHeight: '400px' }} />
          {/* Overlay */}
          <div className="absolute inset-0 bg-gradient-to-r from-transparent to-white/0 lg:to-white/0"></div>
          {/* Floating credential card */}
          <div className="absolute bottom-8 left-8 right-8 lg:right-auto lg:max-w-[240px] bg-jc-charcoal/90 backdrop-blur-sm rounded-2xl px-5 py-5">
            <div className="flex items-center gap-2.5 mb-3">
              {[...Array(5)].map((_, i) => <i key={i} className="ri-star-fill text-jc-gold text-xs"></i>)}
            </div>
            <p className="text-white text-sm font-serif italic leading-snug mb-2">
              &ldquo;The meals are absolutely delicious!&rdquo;
            </p>
            <p className="text-white/40 text-xs">— Laura, Weekly Meal Client</p>
          </div>
        </div>

        {/* Text panel */}
        <div
          ref={textAnim.ref}
          className="w-full lg:w-[55%] flex items-center bg-jc-cream"
          style={{
            opacity: textAnim.visible ? 1 : 0,
            transform: textAnim.visible ? 'translateX(0)' : 'translateX(40px)',
            transition: 'opacity 0.8s ease 0.15s, transform 0.8s ease 0.15s',
          }}
        >
          <div className="px-8 md:px-14 lg:px-16 py-16 md:py-20 w-full">
            <div className="flex items-center gap-3 mb-5">
              <span className="w-8 h-px bg-jc-gold"></span>
              <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-jc-gold">{eyebrow}</span>
            </div>
            <h2 className="font-serif text-[clamp(1.9rem,3.5vw,3rem)] font-light text-jc-charcoal leading-[1.12] mb-10">
              {headline}
            </h2>
            <div className="space-y-7">
              {benefits.map((b, i) => (
                <div
                  key={i}
                  className="flex items-start gap-4 group"
                  style={{
                    opacity: textAnim.visible ? 1 : 0,
                    transform: textAnim.visible ? 'translateY(0)' : 'translateY(16px)',
                    transition: `opacity 0.5s ease ${0.3 + i * 0.1}s, transform 0.5s ease ${0.3 + i * 0.1}s`,
                  }}
                >
                  <div className="w-11 h-11 flex items-center justify-center bg-white rounded-xl flex-shrink-0 group-hover:bg-jc-gold/8 transition-colors duration-300">
                    <i className={`${b.icon} text-jc-gold text-xl`}></i>
                  </div>
                  <div>
                    <strong className="block font-serif text-[1.1rem] text-jc-charcoal mb-1 leading-tight">{b.title}</strong>
                    <p className="text-gray-400 text-sm leading-relaxed">{b.description}</p>
                  </div>
                </div>
              ))}
            </div>
            {cta && (
              <div className="mt-11">
                <a href={cta.href}
                  className="inline-flex items-center gap-2.5 px-7 py-3.5 bg-jc-charcoal text-white text-[10px] font-bold tracking-[0.18em] uppercase rounded-full hover:bg-jc-gold hover:text-jc-charcoal transition-all duration-300 whitespace-nowrap group">
                  {cta.text} <i className="ri-arrow-right-line transition-transform duration-300 group-hover:translate-x-1"></i>
                </a>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
